import { useState, useEffect, memo } from 'react'
import { cloneDeep } from 'lodash'
import { differenceInMilliseconds } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import {
  MapContainer,
  TileLayer,
  Polygon,
  Polyline,
  Marker,
} from 'react-leaflet'
import { langGPS } from '../assets/langGPS'

import {
  useSessionWS,
  useRestricted,
  useTrkptsWS,
  useListSessionsWS,
  useLang,
  useUser,
} from '../utils/hooks'

import ChangeView from '../components/ChangeView'
import SpeedChart from '../components/SpeedChart'
import Help from '../components/Help'
import HelpNotif from '../components/HelpNotif'

import { red } from '../components/MarkerIcons/MarkerIcons'

import {
  getSessionData,
  getMapData,
  checkTrkptIsInRange,
  getChartData,
  getStrFromTrkpts,
  getSpeedAndDistanceFromTrkpts,
} from '../assets/assets_functions.js'

import {
  StyledMapContainer,
  Loader,
  LoaderWrapper,
} from '../components/StyledElements'

import { blueOptions, kGPSDataView } from '../assets/colors'
import { useRef } from 'react'

const ModifySession = ({ handleUpdateStats }) => {
  const navigateTo = useNavigate()

  const firstDiv = useRef(null)

  const { restricted, toggleRestricted } = useRestricted()
  const { sessionWS, changeSessionWS } = useSessionWS()
  const { trkptsWS, changeTrkptsWS } = useTrkptsWS()
  const { listSessionsWS, setListSessionsWS } = useListSessionsWS()
  const { user } = useUser()

  const { lang } = useLang()
  const choosenLang = lang === 'fr' ? langGPS.fr : langGPS.eng
  const demoUserName = process.env.REACT_APP_DEMO_USERNAME

  const [step, setStep] = useState(0)
  const [center, setCenter] = useState({})
  const [restrictedCenter, setRestrictedCenter] = useState({})
  const [polygon, setPolygon] = useState({})
  const [restrictedPolygon, setRestrictedPolygon] = useState({})
  const [timeDomain, setTimeDomain] = useState([])
  const [restrictedTimeDomain, setRestrictedTimeDomain] = useState([])
  const [chartOptions, setChartOptions] = useState(null)
  const [mapPath, setMapPath] = useState(null)
  const [maxSpeed, setMaxSpeed] = useState()
  const [markers, setMarkers] = useState([])
  const [trkpts, setTrkpts] = useState({
    brut: [],
    origin: '',
  })
  const [session, setSession] = useState(null)
  const [hasExcludedPoints, setHasExcludedPoints] = useState(false)
  const [saveDone, setSaveDone] = useState(false)

  const [showHelp, setShowHelp] = useState(false)

  useEffect(() => {
    if (!sessionWS) {
      const localUserId = localStorage.getItem('wsurf_userId')
      if (!localUserId || Number(localUserId) === -1) navigateTo('/login')
      else navigateTo('/sessions/')
    }
  }, [])

  useEffect(() => {
    if (!sessionWS) return
    setTrkpts({ brut: trkptsWS.brut, origin: 'modify' })
    setSession(sessionWS)
  }, [trkptsWS, sessionWS])

  useEffect(() => {
    if (!saveDone) return
    const url = '/api/tzip/updateTZip'

    const toBeCompressedStr = getStrFromTrkpts(trkpts.brut)

    const new_data = JSON.stringify({
      trkpts: toBeCompressedStr,
      sessionId: session._id,
    })
    fetch(url, {
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      body: new_data,
    })
      .then((response) => {
        response.json().then((data) => {
          /* console.log('ok') */
          setIsLoading(false)
          const temp = Array.from(listSessionsWS.data)
          const temp_index = temp.findIndex(
            (session) => session._id === sessionWS._id,
          )
          // const updatedSession = {
          //   ...temp[temp_index],
          //   restrictedStartSession: new Date(restrictedTimeDomain[0]),
          //   restrictedSessionMinLat: restrictedPolygon[3][0],
          //   restrictedSessionMaxLat: restrictedPolygon[0][0],
          //   restrictedSessionMinLng: restrictedPolygon[1][1],
          //   restrictedSessionMaxLng: restrictedPolygon[0][1],
          //   restrictedSessionDuration:
          //     differenceInMilliseconds(
          //       restrictedTimeDomain[1],
          //       restrictedTimeDomain[0],
          //     ) / 1000,
          // }
          temp[temp_index] = session
          setListSessionsWS({ type: 'CHANGED_SESSION_RESTR', payload: temp })
          changeSessionWS(session)
          //navigateTo('/sessions/')
        })
      })
      .catch((error) => {
        console.log('Error adding session', error)
      })
    setIsLoading(false)
  }, [saveDone])

  useEffect(() => {
    if (trkpts.brut.length === 0) return
    if (!session) return
    if (saveDone) return

    const { speed, speedTime, distance } = getSpeedAndDistanceFromTrkpts(
      trkpts.brut,
    )
    const { chartOptions } = getChartData(
      trkpts.brut,
      session.timeZone,
      false,
      'modify',
      null,
    )
    const {
      center,
      polygon,
      timeDomain,
      restrictedCenter,
      restrictedPolygon,
      restrictedTimeDomain,
    } = getSessionData(trkpts.brut)

    const { mapPath } = getMapData(trkpts.brut, false)
    setCenter(center)
    setPolygon(polygon)
    setTimeDomain(timeDomain)
    setRestrictedCenter(restrictedCenter)
    setRestrictedPolygon(restrictedPolygon)
    setRestrictedTimeDomain(restrictedTimeDomain)
    setMaxSpeed(speed)
    setChartOptions(chartOptions)
    setMapPath(mapPath)
  }, [session])

  const [isLoading, setIsLoading] = useState(false)

  const handleUpdateMapMarkers = (values) => {
    if (values.length > 0) {
      let markers = []
      const temp_trkpts = trkpts.brut
      temp_trkpts.forEach((trkpt) => {
        let i = 0
        let flag = false
        while (i < values.length && !flag) {
          flag = checkTrkptIsInRange(values[i], trkpt)
          i++
        }

        if (flag) {
          markers.push(<Marker position={[trkpt.lat, trkpt.lng]} icon={red} />)
        }
      })
      setMarkers(markers)
    } else {
      setMarkers([])
    }
  }

  const handleApplySelection = (values) => {
    const temp_trkpts = cloneDeep(trkpts.brut)
    temp_trkpts.forEach((trkpt) => {
      let i = 0
      let flag = false
      while (i < values.length && !flag) {
        flag = checkTrkptIsInRange(values[i], trkpt)
        i++
      }

      if (flag) {
        trkpt.excluded = 1
      }
    })
    setTrkpts({ ...trkpts, brut: temp_trkpts })
    setHasExcludedPoints(true)
    if (!restricted) toggleRestricted()
    setMarkers([])
  }

  const handleSeeStats = () => {
    setStep(2)
  }

  const handleCancel = () => {
    const temp_trkpts = cloneDeep(trkpts.brut)
    temp_trkpts.forEach((trkpt) => (trkpt.excluded = 0))
    setHasExcludedPoints(false)
    setTrkpts({ ...trkpts, brut: temp_trkpts })
    setMarkers([])
  }

  const handleSave = (values) => {
    setIsLoading(true)
    const temp_trkpts = trkpts.brut.map((trkpt) => {
      trkpt.excluded = 0
      return trkpt
    })

    temp_trkpts.forEach((trkpt) => {
      let i = 0
      let flag = false
      while (i < values.length && !flag) {
        flag = checkTrkptIsInRange(values[i], trkpt)
        i++
      }

      if (flag) {
        trkpt.excluded = 1
      }
    })

    setTrkpts({ ...trkpts, brut: temp_trkpts })
    changeTrkptsWS({ brut: temp_trkpts, origin: 'modify' })

    const { restrictedPolygon, restrictedTimeDomain } =
      getSessionData(temp_trkpts)
    const { speed, speedTime, distance } = getSpeedAndDistanceFromTrkpts(
      temp_trkpts,
      restricted,
    )

    const url = '/api/sessionWS/modifySessionWS'

    fetch(url, {
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session: {
          restrictedStartSession: new Date(restrictedTimeDomain[0]),
          restrictedSessionMinLat: restrictedPolygon[3][0],
          restrictedSessionMaxLat: restrictedPolygon[0][0],
          restrictedSessionMinLng: restrictedPolygon[1][1],
          restrictedSessionMaxLng: restrictedPolygon[0][1],
          restrictedSessionDuration:
            differenceInMilliseconds(
              restrictedTimeDomain[1],
              restrictedTimeDomain[0],
            ) / 1000,
          sessionId: session._id,
          maxSpeed: Math.round(speed * 10) / 10,
          maxSpeedTime: speedTime,
          distance: Math.round(distance / 100) / 10,
        },
      }),
    })
      .then((response) => {
        response.json().then((data) => {
          /* console.log(data) */
          setSession({
            ...session,
            restrictedStartSession: new Date(restrictedTimeDomain[0]),
            restrictedSessionMinLat: restrictedPolygon[3][0],
            restrictedSessionMaxLat: restrictedPolygon[0][0],
            restrictedSessionMinLng: restrictedPolygon[1][1],
            restrictedSessionMaxLng: restrictedPolygon[0][1],
            restrictedSessionDuration:
              differenceInMilliseconds(
                restrictedTimeDomain[1],
                restrictedTimeDomain[0],
              ) / 1000,
            maxSpeed: Math.round(speed * 10) / 10,
            maxSpeedTime: speedTime,
            distance: Math.round(distance / 100) / 10,
          })

          setSaveDone(true)
          handleUpdateStats(kGPSDataView)
        })
      })
      .catch((error) => {
        console.log('Error updating session', error)
        setIsLoading(false)
      })
  }

  const handleHelp = (what) => {
    setShowHelp(what)
  }

  return (
    <section className="section pt-0 pl-0 pb-0" ref={firstDiv}>
      {!showHelp && <Help handleHelp={handleHelp} />}
      {showHelp && (
        <HelpNotif handleHelp={handleHelp} helpText={choosenLang.help} />
      )}
      {mapPath && (
        <div>
          {isLoading && (
            <div className="notification is-white block">
              <LoaderWrapper>
                <Loader data-testid="loader" />
              </LoaderWrapper>
            </div>
          )}
          <StyledMapContainer>
            <MapContainer scrollWheelZoom={true} maxZoom={18} zoomOffset={-1}>
              <ChangeView
                center={center}
                origin={'modify'}
                map_bounds={polygon}
                map_path={mapPath.positions}
              />
              <TileLayer
                attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                zIndex="0"
              />

              {!mapPath && (
                <Polygon pathOptions={blueOptions} positions={polygon} />
              )}
              <Polyline
                pathOptions={blueOptions}
                positions={mapPath.positions}
              />
              {markers.length > 0 && markers}
            </MapContainer>
          </StyledMapContainer>

          <SpeedChart
            origin={'modify'}
            loading={isLoading}
            options={chartOptions}
            timeDomain={timeDomain}
            handleUpdateMapMarkers={handleUpdateMapMarkers}
            handleApplySelection={handleApplySelection}
            handleSeeStats={handleSeeStats}
            handleCancel={handleCancel}
            handleSave={handleSave}
            disabledDemo={user.userName === demoUserName}
          />
        </div>
      )}
    </section>
  )
}

export default memo(ModifySession)
